import { Button, Input } from "@omnitok/ui";
import {
  ArrowRight,
  FolderOpen,
  GripVertical,
  Inbox,
  Plus,
  RefreshCw,
} from "lucide-react";
import {
  Callout,
  EmptyState,
  PageContainer,
  PageHeader,
} from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useT } from "@/i18n";
import { draftsApi, projectsApi } from "@/lib/builder-api";
import { findIconComponent } from "@/lib/project-icons";
import {
  resolveLocalized,
  type DisplayLabel,
} from "@pervasivemind/dashboard-render";
import type { Project, ReportDraft } from "@/types";

/** Pseudo-slug for drafts without a project (routes to a reduced hub). */
export const UNASSIGNED_SLUG = "_unassigned";

interface ProjectCardData {
  project: Project | null; // null = the "unassigned drafts" bucket
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  total: number;
  published: number;
}

/**
 * Home — one card per project (PR-0 builder reorg).
 *
 * Replaces the retired ``BuilderListPage`` (projects with their report
 * tables inline): that layout didn't scale as projects accumulate. The
 * grid shows only project cards; reports live inside each project's
 * hub (``/projects/:slug``). Orphan drafts surface as an "Unclassified"
 * card only when they exist.
 */
export function ProjectsHomePage(): JSX.Element {
  const { t, locale } = useT();
  const navigate = useNavigate();

  const [drafts, setDrafts] = useState<ReportDraft[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [draftsRes, projectsRes] = await Promise.all([
        draftsApi.list(),
        projectsApi.list(),
      ]);
      setDrafts(draftsRes.drafts);
      setProjects(projectsRes.projects);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const cards = useMemo<ProjectCardData[]>(() => {
    const byProject = new Map<string, ReportDraft[]>();
    const unassigned: ReportDraft[] = [];
    for (const draft of drafts) {
      if (draft.project_id) {
        const arr = byProject.get(draft.project_id) ?? [];
        arr.push(draft);
        byProject.set(draft.project_id, arr);
      } else {
        unassigned.push(draft);
      }
    }

    const out: ProjectCardData[] = projects.map((project) => {
      const projectDrafts = byProject.get(project.id) ?? [];
      return {
        project,
        name:
          resolveLocalized(project.name as DisplayLabel, locale) ||
          project.slug,
        slug: project.slug,
        description: project.description ?? undefined,
        icon: project.icon ?? undefined,
        total: projectDrafts.length,
        published: projectDrafts.filter((d) => d.status === "published")
          .length,
      };
    });

    if (unassigned.length > 0) {
      out.push({
        project: null,
        name: t("home.unassigned"),
        slug: UNASSIGNED_SLUG,
        description: t("home.unassigned.desc"),
        total: unassigned.length,
        published: unassigned.filter((d) => d.status === "published").length,
      });
    }
    return out;
  }, [drafts, projects, locale, t]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.slug.toLowerCase().includes(q) ||
        (c.description ?? "").toLowerCase().includes(q),
    );
  }, [cards, query]);

  // ── Sidebar-order DnD (2026-07-22) ────────────────────────────────
  // Drag a project card to a new position; the whole slug order is
  // persisted (``projectsApi.reorderProjects``) and the portal sidebar
  // reflects it in ~60 s. Only REAL projects are draggable (never the
  // "unassigned" bucket), and only when NOT searching — reordering a
  // filtered subset would be ambiguous. HTML5 native, handle-armed
  // (mirrors the report DnD in ``DraftsTable``): the grip arms the card
  // so a plain click still opens it.
  const reorderEnabled = query.trim() === "";
  const [armedSlug, setArmedSlug] = useState<string | null>(null);
  const [dragSlug, setDragSlug] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    slug: string;
    after: boolean;
  } | null>(null);

  const clearDrag = useCallback(() => {
    setArmedSlug(null);
    setDragSlug(null);
    setDropTarget(null);
  }, []);

  const handleDrop = useCallback(() => {
    if (!dragSlug || !dropTarget || dropTarget.slug === dragSlug) {
      clearDrag();
      return;
    }
    const current = projects.map((p) => p.slug);
    const without = current.filter((s) => s !== dragSlug);
    const anchor = without.indexOf(dropTarget.slug);
    if (anchor === -1) {
      clearDrag();
      return;
    }
    const at = anchor + (dropTarget.after ? 1 : 0);
    const nextSlugs = [...without.slice(0, at), dragSlug, ...without.slice(at)];
    const unchanged = nextSlugs.every((s, i) => projects[i]?.slug === s);
    clearDrag();
    if (unchanged) return;
    const bySlug = new Map(projects.map((p): [string, Project] => [p.slug, p]));
    const reordered = nextSlugs
      .map((s) => bySlug.get(s))
      .filter((p): p is Project => p != null);
    const prev = projects;
    setProjects(reordered); // optimistic
    projectsApi.reorderProjects(nextSlugs).catch((err: unknown) => {
      setProjects(prev); // revert on failure — never leave a phantom order
      setError(err instanceof Error ? err.message : t("common.error"));
    });
  }, [dragSlug, dropTarget, projects, clearDrag, t]);

  return (
    <PageContainer>
      <PageHeader
        title={t("home.title")}
        subtitle={t("home.subtitle")}
        actions={
          <>
            <Button
              ghost
              size="sm"
              onClick={() => void load()}
              disabled={loading}
              title={t("common.refresh")}
              leftIcon={
                <RefreshCw
                  size={14}
                  className={loading ? "animate-spin" : undefined}
                />
              }
            >
              <span className="sr-only">{t("common.refresh")}</span>
            </Button>
            <Input
              value={query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setQuery(e.target.value)
              }
              placeholder={t("home.search")}
              className="w-56"
            />
            <Button
              variant="primary"
              leftIcon={<Plus size={16} />}
              onClick={() => navigate("/projects/new")}
            >
              {t("list.newProject")}
            </Button>
          </>
        }
      />

      {error !== null && (
        <Callout tone="danger" className="mb-4">
          <div className="flex items-start gap-3">
            <span className="flex-1">{error}</span>
            <Button variant="danger" ghost size="xs" onClick={() => void load()}>
              {t("common.retry")}
            </Button>
          </div>
        </Callout>
      )}

      {loading && cards.length === 0 ? (
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
          <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-neutral-500">
            <Spinner size={16} ariaLabel={t("common.loading")} />
            {t("common.loading")}
          </div>
        </div>
      ) : cards.length === 0 ? (
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
          <EmptyState
            icon={<FolderOpen size={24} />}
            title={t("home.title")}
            description={t("list.empty")}
            action={
              <Button
                variant="primary"
                leftIcon={<Plus size={16} />}
                onClick={() => navigate("/projects/new")}
              >
                {t("list.newProject")}
              </Button>
            }
          />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((card) => {
            const open = () => navigate(`/projects/${card.slug}`);
            // Only real projects reorder, and only when not searching.
            if (!reorderEnabled || card.project === null) {
              return (
                <ProjectCard key={card.slug} card={card} onOpen={open} />
              );
            }
            const slug = card.slug;
            const isDragging = dragSlug === slug;
            const isTarget = dropTarget?.slug === slug && dragSlug !== slug;
            return (
              <div
                key={card.slug}
                draggable={armedSlug === slug}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = "move";
                  // Firefox needs data set for the drag to start.
                  e.dataTransfer.setData("text/plain", slug);
                  setDragSlug(slug);
                }}
                onDragOver={(e) => {
                  if (!dragSlug) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  const rect = e.currentTarget.getBoundingClientRect();
                  // Grid flows left-to-right → split on the horizontal half.
                  const after = e.clientX > rect.left + rect.width / 2;
                  setDropTarget((prev) =>
                    prev?.slug === slug && prev.after === after
                      ? prev
                      : { slug, after },
                  );
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  handleDrop();
                }}
                onDragEnd={clearDrag}
                className={`relative rounded-xl transition-opacity ${
                  isDragging ? "opacity-40" : ""
                } ${isTarget ? "ring-2 ring-primary ring-offset-2" : ""}`}
              >
                <ProjectCard card={card} onOpen={open} />
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={t("list.dragHandle")}
                  title={t("list.dragHandle")}
                  onMouseDown={() => setArmedSlug(slug)}
                  onMouseUp={() => setArmedSlug(null)}
                  onClick={(e) => e.stopPropagation()}
                  /* No background. `bg-white/70` is a translucent WHITE:
                     over a white card it is invisible, and over the dark
                     theme it painted a light-grey square in the corner of
                     every project card. The dark override rewrites
                     `.bg-white`, not `.bg-white\/70` — a different class —
                     which is exactly the failure mode finding T5
                     describes. The handle reads from its glyph. */
                  className="absolute right-2 top-2 z-10 inline-flex cursor-grab touch-none items-center rounded-md p-0.5 text-neutral-500 transition-colors hover:text-neutral-600 active:cursor-grabbing"
                >
                  <GripVertical size={15} />
                </span>
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => navigate("/projects/new")}
            className="flex h-full w-full min-h-[10rem] items-center justify-center gap-2 rounded-xl border-2 border-dashed border-neutral-300 text-sm font-medium text-neutral-500 transition-colors hover:border-primary hover:bg-primary-soft hover:text-primary"
          >
            <Plus size={16} />
            {t("list.newProject")}
          </button>
        </div>
      )}
    </PageContainer>
  );
}

function ProjectCard({
  card,
  onOpen,
}: {
  card: ProjectCardData;
  onOpen: () => void;
}): JSX.Element {
  const { t } = useT();
  const Icon = card.project ? findIconComponent(card.icon) : Inbox;
  const IconComponent = Icon ?? FolderOpen;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex h-full w-full min-h-[10rem] flex-col rounded-xl border border-neutral-200 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
          <IconComponent size={20} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-neutral-900">
            {card.name}
          </span>
          <span className="block truncate font-mono text-xs text-neutral-500">
            {card.slug}
          </span>
        </span>
      </div>
      {card.description && (
        <p className="mt-2 line-clamp-2 text-[13px] text-neutral-500">
          {card.description}
        </p>
      )}
      <div className="mt-auto flex items-center gap-3 pt-3 text-[13px] text-neutral-500">
        <span>
          <b className="font-semibold text-neutral-900">{card.total}</b>{" "}
          {t("home.reports")}
        </span>
        <span>
          <b className="font-semibold text-neutral-900">{card.published}</b>{" "}
          {t("home.published")}
        </span>
        <ArrowRight
          size={14}
          className="ml-auto text-neutral-500 transition-colors group-hover:text-primary"
        />
      </div>
    </button>
  );
}
